import { useState, useEffect } from 'react';
import { Shield, Send, CheckCircle, XCircle, Eye, EyeOff, Key, Trash2, AlertTriangle } from 'lucide-react';
import { adminAxios } from './AdminLayout';

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    brevo_api_key: '',
    notification_email: '',
    reply_to_emails: ['', '', '', '', '', ''],
    sender_name: '',
    sender_email: '',
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [loadingDiag, setLoadingDiag] = useState(false);
  const [message, setMessage] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [termsEnabled, setTermsEnabled] = useState(true);
  const [termsHtml, setTermsHtml] = useState('');

  const refresh = async () => {
    const res = await adminAxios().get('/api/admin/settings');
    setSettings(res.data);
    setTermsEnabled(res.data.commercial_terms_enabled !== false);
    setTermsHtml(res.data.commercial_terms_html || '');
    setForm(function(prev) {
      return {
        brevo_api_key: '',
        notification_email: res.data.notification_email || '',
        reply_to_emails: res.data.reply_to_emails || ['', '', '', '', '', ''],
        sender_name: res.data.sender_name || '',
        sender_email: res.data.sender_email || '',
      };
    });
  };

  useEffect(function() { refresh(); }, []);

  const handleSave = async function(e) {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      var payload = {};
      if (form.brevo_api_key) payload.brevo_api_key = form.brevo_api_key;
      if (form.notification_email) payload.notification_email = form.notification_email;
      if (form.reply_to_emails) payload.reply_to_emails = form.reply_to_emails.filter(Boolean);
      if (form.sender_name) payload.sender_name = form.sender_name;
      if (form.sender_email) payload.sender_email = form.sender_email;
      await adminAxios().put('/api/admin/settings', payload);
      await refresh();
      setMessage({ type: 'success', text: 'Settings saved' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async function() {
    setTesting(true);
    setMessage(null);
    try {
      var res = await adminAxios().post('/api/admin/settings/test-email');
      setMessage({ type: 'success', text: res.data.message });
      await refresh();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const handleValidateKey = async function() {
    setValidating(true);
    setMessage(null);
    try {
      var res = await adminAxios().post('/api/admin/settings/validate-key');
      setMessage({ type: 'success', text: res.data.message });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Validation failed' });
    } finally {
      setValidating(false);
    }
  };

  const handleClearKey = async function() {
    if (!window.confirm('Remove the saved Brevo key from the database? You will need to enter a new key.')) return;
    setClearing(true);
    setMessage(null);
    try {
      var res = await adminAxios().post('/api/admin/settings/clear-key');
      setMessage({ type: 'success', text: res.data.message });
      await refresh();
      setDiagnostics(null);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Clear failed' });
    } finally {
      setClearing(false);
    }
  };

  const handleDiagnostics = async function() {
    setLoadingDiag(true);
    try {
      // Run full Brevo health check: preflight + diagnostic send
      var preflight = await adminAxios().post('/api/admin/campaigns/preflight');
      var healthResult = { preflight: preflight.data, send: null };

      // Only attempt diagnostic send if preflight passes
      if (preflight.data.can_send) {
        try {
          var send = await adminAxios().post('/api/admin/brevo/diagnostic-send', { to_email: 'david@realtechservices.co.uk', to_name: 'David' });
          healthResult.send = send.data;
        } catch (e) {
          healthResult.send = { success: false, error: e.response?.data?.detail || 'Send failed' };
        }
      }

      // Also get key diagnostics
      var keyDiag = await adminAxios().get('/api/admin/settings/diagnostics');
      healthResult.key = keyDiag.data;

      setDiagnostics(healthResult);
    } catch (err) {
      setDiagnostics({ error: err.response?.data?.detail || 'Health check failed' });
    } finally {
      setLoadingDiag(false);
    }
  };

  if (!settings) return <div className="animate-pulse font-manrope text-slate-500">Loading settings...</div>;

  var statusColor = settings.email_service_status === 'verified' ? 'bg-green-100' : settings.email_service_status === 'configured' ? 'bg-yellow-100' : 'bg-red-100';
  var statusLabel = settings.email_service_status === 'verified' ? 'Verified' : settings.email_service_status === 'configured' ? 'Configured (not yet verified)' : 'Not Connected';

  return (
    <div data-testid="admin-settings-page">
      <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-6">Settings</h2>

      {/* Status Bar */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={'w-10 h-10 rounded-xl flex items-center justify-center ' + statusColor}>
              {settings.email_service_status === 'verified' ? <CheckCircle className="w-5 h-5 text-green-600" /> : settings.email_service_status === 'configured' ? <Shield className="w-5 h-5 text-yellow-600" /> : <XCircle className="w-5 h-5 text-red-500" />}
            </div>
            <div>
              <p className="font-manrope font-semibold text-slate-900">Email Service: {statusLabel}</p>
              <p className="font-manrope text-sm text-slate-500">
                {settings.brevo_api_key_set ? 'Key: ' + settings.brevo_api_key_masked + ' (source: ' + settings.brevo_key_source + ')' : 'No API key configured'}
              </p>
            </div>
          </div>
          {settings.brevo_api_key_set && (
            <div className="flex items-center gap-2">
              <button onClick={handleValidateKey} disabled={validating} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 font-manrope text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid="validate-key-button">
                <Key className="w-4 h-4" /> {validating ? 'Checking...' : 'Validate Key'}
              </button>
              <button onClick={handleTestEmail} disabled={testing} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0096D6] text-white font-manrope text-sm font-semibold disabled:opacity-50" data-testid="test-email-button">
                <Send className="w-4 h-4" /> {testing ? 'Sending...' : 'Test Email'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* MCP Key Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="font-manrope text-sm text-amber-800">
          <strong>Important:</strong> Use a standard Brevo SMTP API key, not an MCP key. MCP keys are for AI assistant integrations and will not work for website email sending. Get your key from <strong>Brevo &gt; SMTP &amp; API &gt; API Keys</strong>.
        </div>
      </div>

      {/* Settings Form */}
      <form onSubmit={handleSave} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm space-y-6 mb-6">
        <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
          <Shield className="w-5 h-5 text-[#0096D6]" />
          <h3 className="font-outfit text-lg font-semibold text-slate-900">Email Configuration</h3>
        </div>

        <div>
          <label className="font-manrope text-sm font-medium text-slate-700 block mb-1">Brevo API Key</label>
          <div className="relative">
            <input type={showKey ? 'text' : 'password'} value={form.brevo_api_key} onChange={function(e) { setForm(function(p) { return Object.assign({}, p, {brevo_api_key: e.target.value}); }); }} className="w-full px-4 py-3 pr-12 rounded-xl border border-slate-200 font-manrope text-sm" placeholder={settings.brevo_api_key_set ? 'Enter new key to replace existing' : 'Paste your Brevo API key here'} data-testid="settings-brevo-key-input" autoComplete="off" />
            <button type="button" onClick={function() { setShowKey(!showKey); }} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600">
              {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <p className="font-manrope text-xs text-slate-400 mt-1">Stored securely server-side. Never exposed to the browser after saving. You can also set BREVO_API_KEY as a server environment variable.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="font-manrope text-sm font-medium text-slate-700 block mb-1">Admin Notification Email</label>
            <input type="email" value={form.notification_email} onChange={function(e) { setForm(function(p) { return Object.assign({}, p, {notification_email: e.target.value}); }); }} className="w-full px-4 py-3 rounded-xl border border-slate-200 font-manrope text-sm" data-testid="settings-notification-email" />
          </div>
          <div>
            <label className="font-manrope text-sm font-medium text-slate-700 block mb-1">Sender Name</label>
            <input type="text" value={form.sender_name} onChange={function(e) { setForm(function(p) { return Object.assign({}, p, {sender_name: e.target.value}); }); }} className="w-full px-4 py-3 rounded-xl border border-slate-200 font-manrope text-sm" data-testid="settings-sender-name" />
          </div>
          <div>
            <label className="font-manrope text-sm font-medium text-slate-700 block mb-1">Sender Email</label>
            <input type="email" value={form.sender_email} onChange={function(e) { setForm(function(p) { return Object.assign({}, p, {sender_email: e.target.value}); }); }} className="w-full px-4 py-3 rounded-xl border border-slate-200 font-manrope text-sm" data-testid="settings-sender-email" />
          </div>
        </div>

        <div>
          <label className="font-manrope text-sm font-medium text-slate-700 block mb-2">Reply-To Email Addresses (up to 6)</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[0,1,2,3,4,5].map(function(idx) {
              var emails = form.reply_to_emails || [];
              return <input key={idx} type="email" value={emails[idx] || ''} onChange={function(e) {
                var ne = (form.reply_to_emails || ['','','','','','']).slice();
                while (ne.length <= idx) ne.push('');
                ne[idx] = e.target.value;
                setForm(function(p) { return Object.assign({}, p, {reply_to_emails: ne}); });
              }} className="px-4 py-3 rounded-xl border border-slate-200 font-manrope text-sm" placeholder={'Reply-To Email ' + (idx + 1)} />;
            })}
          </div>
          <p className="font-manrope text-xs text-slate-400 mt-1">Leave unused fields blank. Select which addresses to use when creating each offer.</p>
        </div>

        {message && (
          <div className={'flex items-center gap-2 p-4 rounded-xl text-sm font-manrope ' + (message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700')} data-testid="settings-message">
            {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {message.text}
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            {settings.brevo_api_key_set && settings.brevo_key_source === 'MongoDB' && (
              <button type="button" onClick={handleClearKey} disabled={clearing} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-red-200 text-red-600 font-manrope text-sm hover:bg-red-50 disabled:opacity-50" data-testid="clear-key-button">
                <Trash2 className="w-4 h-4" /> {clearing ? 'Clearing...' : 'Clear Saved Key'}
              </button>
            )}
          </div>
          <button type="submit" disabled={saving} className="px-6 py-3 rounded-xl bg-[#0096D6] text-white font-manrope text-sm font-semibold disabled:opacity-50" data-testid="settings-save-button">
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      {/* Commercial Terms */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-outfit text-lg font-semibold text-slate-900">Global Commercial Terms</h3>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 font-manrope text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={termsEnabled} onChange={function(e) { setTermsEnabled(e.target.checked); }} className="w-4 h-4 rounded" />
              Auto-include in emails
            </label>
          </div>
        </div>
        <p className="font-manrope text-xs text-slate-500 mb-3">These terms appear automatically at the bottom of all offer emails and broadcasts. Leave blank to use the default professional terms.</p>
        <textarea value={termsHtml} onChange={function(e) { setTermsHtml(e.target.value); }} className="w-full px-4 py-3 rounded-xl border border-slate-200 font-manrope text-sm h-40 resize-none" placeholder="Paste custom HTML for your commercial terms, or leave blank for default..." />
        <div className="flex justify-end mt-3">
          <button type="button" onClick={async function() {
            setMessage(null);
            try {
              await adminAxios().put('/api/admin/settings', { commercial_terms_enabled: termsEnabled, commercial_terms_html: termsHtml });
              setMessage({ type: 'success', text: 'Commercial terms saved' });
            } catch (err) { setMessage({ type: 'error', text: 'Failed to save terms' }); }
          }} className="px-5 py-2.5 rounded-xl bg-[#0096D6] text-white font-manrope text-sm font-semibold">
            Save Terms
          </button>
        </div>
      </div>

      {/* Brevo Health Check */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-outfit text-lg font-semibold text-slate-900">Brevo Health Check</h3>
          <button onClick={handleDiagnostics} disabled={loadingDiag} className="px-4 py-2 rounded-xl bg-amber-500 text-white font-manrope text-sm font-semibold disabled:opacity-50" data-testid="run-diagnostics-button">
            {loadingDiag ? 'Running Full Check...' : 'Run Brevo Health Check'}
          </button>
        </div>
        <p className="font-manrope text-xs text-slate-500 mb-3">Tests: API key → Sender verification → Single email send to david@realtechservices.co.uk</p>
        {diagnostics && (
          <div className="space-y-3" data-testid="diagnostics-panel">
            {diagnostics.error && <div className="bg-red-50 rounded-xl p-4 text-red-600 font-manrope text-sm">{diagnostics.error}</div>}

            {/* Preflight */}
            {diagnostics.preflight && (
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-manrope text-xs font-bold text-slate-600 mb-2">PREFLIGHT</p>
                <div className="grid grid-cols-2 gap-2 font-mono text-xs">
                  <p>API Key: <span className={diagnostics.preflight.api_key_valid ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>{diagnostics.preflight.api_key_valid ? 'VALID' : 'INVALID'}</span></p>
                  <p>Sender: <span className={diagnostics.preflight.sender_valid ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>{diagnostics.preflight.sender_valid ? 'VERIFIED' : 'NOT VERIFIED'}</span></p>
                  <p>Can Send: <span className={diagnostics.preflight.can_send ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>{diagnostics.preflight.can_send ? 'YES' : 'NO'}</span></p>
                  {diagnostics.preflight.warning && <p className="text-amber-600">{diagnostics.preflight.warning}</p>}
                  {diagnostics.preflight.sender_check_status && <p>Sender Check: {diagnostics.preflight.sender_check_status}</p>}
                  {diagnostics.preflight.account_info && <p>Account: {diagnostics.preflight.account_info}</p>}
                  {diagnostics.preflight.timings && <p className="text-slate-400">Timings: account={diagnostics.preflight.timings.account || '-'}s, senders={diagnostics.preflight.timings.senders || '-'}s</p>}
                </div>
                {(diagnostics.preflight.errors || []).length > 0 && (
                  <div className="mt-2 bg-red-50 rounded-lg p-3 text-red-600 text-xs font-mono">
                    {diagnostics.preflight.errors.map(function(e, i) { return <p key={i}>{e.code}: {e.message || e.body || ''}</p>; })}
                  </div>
                )}
              </div>
            )}

            {/* Diagnostic Send */}
            {diagnostics.send && (
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-manrope text-xs font-bold text-slate-600 mb-2">DIAGNOSTIC SEND</p>
                <div className="font-mono text-xs space-y-1">
                  <p>Result: <span className={diagnostics.send.success ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>{diagnostics.send.success ? 'SUCCESS' : 'FAILED'}</span></p>
                  <p>Brevo Status: {diagnostics.send.brevo_status_code || diagnostics.send.status_code || '-'}</p>
                  {diagnostics.send.brevo_message_id && <p>Message ID: <span className="text-green-600">{diagnostics.send.brevo_message_id}</span></p>}
                  {diagnostics.send.message_id && <p>Message ID: <span className="text-green-600">{diagnostics.send.message_id}</span></p>}
                  {diagnostics.send.error && <p className="text-red-600">Error: {diagnostics.send.error}</p>}
                  {diagnostics.send.failure_code && <p className="text-red-600">Code: {diagnostics.send.failure_code}</p>}
                  {diagnostics.send.raw_response && <p className="text-slate-500 break-all">Raw: {JSON.stringify(diagnostics.send.raw_response).substring(0, 300)}</p>}
                </div>
              </div>
            )}

            {!diagnostics.preflight?.can_send && diagnostics.preflight && (
              <div className="bg-red-50 rounded-xl p-4 text-red-700 font-manrope text-sm">
                <strong>Campaigns are BLOCKED.</strong> Fix the Brevo errors above before sending any offers.
              </div>
            )}

            {/* Key diagnostics */}
            {diagnostics.key && (
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-manrope text-xs font-bold text-slate-600 mb-2">KEY DETAILS</p>
                <div className="font-mono text-xs space-y-0.5">
                  <p>Source: {diagnostics.key.source} · Length: {diagnostics.key.key_length} · Prefix: {diagnostics.key.first_8_chars}</p>
                  {diagnostics.key.server_ip && <p>Server IP: {diagnostics.key.server_ip}</p>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
